import { useMemo, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';

import { getReviewerInfo } from 'apis/liquidationnReviews.api';

import { downloadFileReport, getSummaryReport } from 'apis/summaryReport';
import DynamicLiquidationReviewForm from 'features/LiquidationReview/components/DynamicLiquidationReview';
import ReviewFormMidtermAcceptance from 'features/Project/components/ReviewFormMidtermAcceptance';
import { ProjectProposalDefenseDetail } from 'features/ProjectProposalDefense/pages';
import { Dialog } from 'primereact/dialog';
import { TabPanel, TabView } from 'primereact/tabview';
import { footerConfirmationPrompt } from 'utils/func';
import PropTypes from 'prop-types';
import GeneralCouncilInfo from 'features/Council/components/GeneralInfo';

export default function LiquidationReviewCRDForm({ readOnly }) {
  // #region Data
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);

  const { projectProposalId } = useParams();
  const [searchParams] = useSearchParams();
  const [councilId, councilMemberId, summaryReportId] = [
    searchParams.get('councilId'),
    searchParams.get('councilMemberId'),
    searchParams.get('summaryReportId'),
  ];
  const location = useLocation();
  const { isViewCouncilData } = location?.state || { isViewCouncilData: false };

  const {
    control,
    formState: { errors },
  } = useForm({ mode: 'onChange' });

  const { isDirty, dirtyFields } = useFormState({ control });

  const { data: _summaryReport } = useQuery(
    ['summaryReport', summaryReportId],
    () => getSummaryReport(summaryReportId),
    {
      enabled: summaryReportId !== null,
    }
  );

  const summaryReport = useMemo(() => _summaryReport?.data, [_summaryReport]);

  const { mutate: mutateDownloadFile } = useMutation(downloadFileReport);

  const handleDownloadFile = (filename, fileNameFormat = '') => {
    mutateDownloadFile(
      {
        summaryReportId,
        filename,
      },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = fileNameFormat || filename;

            link.click();
          }
        },
      }
    );
  };

  const { data: infoData, isLoading } = useQuery(
    ['liquidation-reviewer', projectProposalId, councilId],
    () => getReviewerInfo(projectProposalId, councilId)
  );
  const info = useMemo(() => infoData?.data, [infoData]);

  const renderFirstTab = () => (
    <DynamicLiquidationReviewForm
      projectProposalId={projectProposalId}
      councilId={councilId}
      councilMemberId={councilMemberId}
      generalInfo={info?.projectProposal}
      isLoading={isLoading}
      readOnly={readOnly}
    />
  );
  // #endregion

  return (
    <>
      <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields).length}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog
            visible={isActive}
            header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
            position="center"
            style={{ width: '300px' }}
            onHide={onCancel}
            draggable={false}
            footer={() => footerConfirmationPrompt(onConfirm, onCancel, t)}
          >
            <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
          </Dialog>
        )}
      </ReactRouterPrompt>

      <TabView activeIndex={tab} onTabChange={(e) => setTab(e.index)}>
        <TabPanel header={t('council.review.createOrUpdate')} leftIcon="pi pi-file mr-2">
          {renderFirstTab()}
        </TabPanel>
        <TabPanel header={t('council.review.detailProjectProposal')} leftIcon="pi pi-list mr-2">
          <ProjectProposalDefenseDetail />
        </TabPanel>
        <TabPanel header="Thanh Lý" leftIcon="pi pi-list mr-2">
          <ReviewFormMidtermAcceptance
            summaryReport={summaryReport}
            handleDownloadFile={handleDownloadFile}
            control={control}
            errors={errors}
          />
        </TabPanel>
        {isViewCouncilData && (
          <TabPanel header={t('council.review.councilInfo')} leftIcon="pi pi-info-circle mr-2">
            <GeneralCouncilInfo councilId={councilId} projectProposalId={projectProposalId} />
          </TabPanel>
        )}
      </TabView>
    </>
  );
}

LiquidationReviewCRDForm.propTypes = {
  readOnly: PropTypes.bool,
};

LiquidationReviewCRDForm.defaultProps = {
  readOnly: false,
};
